import {FeedbackKind} from './model.js';
import {feedbackPageMarkup} from './page-view.js';

function normaliseKind(value){
  return String(value||'').trim().toLowerCase()===FeedbackKind.IDEA
    ?FeedbackKind.IDEA
    :FeedbackKind.BUG;
}

function pendingItems(rows){
  return Object.freeze((rows??[]).map(row=>Object.freeze({
    requestId:String(row?.requestId??row?.id??''),
    kind:normaliseKind(row?.kind)
  })).filter(item=>item.requestId));
}

function stateMessage(result){
  if(result?.status==='submitted'){
    return {
      kind:'success',
      text:result.duplicate
        ?'To zgłoszenie było już zapisane. Nie utworzyliśmy duplikatu.'
        :'Dziękujemy — zgłoszenie zostało wysłane.'
    };
  }
  if(result?.status==='queued'){
    return {
      kind:'warning',
      text:'Zgłoszenie zapisano lokalnie i wyślemy je po odzyskaniu połączenia.'
    };
  }
  return {
    kind:'error',
    text:'Nie udało się wysłać zgłoszenia. Kod: '+String(result?.errorCode||'FEEDBACK_SUBMIT_FAILED')
  };
}

export function createFeedbackPageController({
  service,
  attachmentProvider=null,
  userId,
  currentScreen=()=> 'unknown',
  isOnline=()=>typeof navigator==='undefined'?true:navigator.onLine
}={}){
  if(
    typeof service?.submit!=='function'
    ||typeof service?.pending!=='function'
    ||typeof service?.retryPending!=='function'
    ||typeof service?.cancel!=='function'
  ){
    throw new TypeError('feedback service is required');
  }
  const owner=String(userId||'').trim();
  if(!owner)throw new TypeError('userId is required');

  let state={
    kind:FeedbackKind.BUG,
    status:'idle',
    messageText:'',
    messageKind:'neutral',
    pendingCount:0,
    pendingItems:Object.freeze([]),
    description:''
  };

  function setState(patch){
    state={...state,...patch};
    return getState();
  }

  function getState(){
    return Object.freeze({...state,pendingItems:Object.freeze([...state.pendingItems])});
  }

  function selectKind(value){
    return setState({
      kind:normaliseKind(value),
      status:'idle',
      messageText:'',
      messageKind:'neutral',
      description:''
    });
  }

  async function readPending(){
    const pending=await service.pending();
    const items=pendingItems(pending);
    setState({pendingCount:items.length,pendingItems:items});
    return items;
  }

  async function refresh(){
    await readPending();
    return getState();
  }

  async function submit({
    kind=state.kind,
    description='',
    includeDiagnostics=true,
    attachmentFile=null
  }={}){
    const feedbackKind=normaliseKind(kind);
    const requestId=service.createRequestId?.();
    const online=Boolean(isOnline());
    let attachment=null;
    let uploaded=false;

    setState({
      kind:feedbackKind,
      status:'submitting',
      messageText:'',
      messageKind:'neutral',
      description:String(description||'')
    });

    try{
      if(attachmentFile&&feedbackKind===FeedbackKind.BUG){
        if(!online||!attachmentProvider?.upload){
          throw Object.assign(new Error('FEEDBACK_ATTACHMENT_REQUIRES_ONLINE'),{
            code:'FEEDBACK_ATTACHMENT_REQUIRES_ONLINE'
          });
        }
        attachment=await attachmentProvider.upload({
          userId:owner,
          requestId,
          file:attachmentFile
        });
        uploaded=true;
      }

      const result=await service.submit({
        requestId,
        userId:owner,
        kind:feedbackKind,
        description,
        currentScreen:String(currentScreen()||'unknown'),
        currentModule:'feedback',
        includeDiagnostics:feedbackKind===FeedbackKind.BUG&&includeDiagnostics!==false,
        attachment
      });

      if(result.status==='failed'&&uploaded){
        try{await attachmentProvider?.remove?.({userId:owner,attachment});}catch{}
      }

      const notice=stateMessage(result);
      const items=await readPending();
      setState({
        status:result.status,
        messageText:notice.text,
        messageKind:notice.kind,
        pendingCount:items.length,
        pendingItems:items,
        description:result.status==='failed'?String(description||''):''
      });
      return result;
    }catch(error){
      if(uploaded){
        try{await attachmentProvider?.remove?.({userId:owner,attachment});}catch{}
      }
      const code=String(error?.code||error?.message||'FEEDBACK_SUBMIT_FAILED').slice(0,80);
      setState({
        status:'failed',
        messageText:code==='FEEDBACK_ATTACHMENT_REQUIRES_ONLINE'
          ?'Screenshot wymaga połączenia. Usuń załącznik albo spróbuj ponownie online.'
          :'Nie udało się przygotować zgłoszenia. Kod: '+code,
        messageKind:'error',
        description:String(description||'')
      });
      throw error;
    }
  }

  async function retryPending(){
    const result=await service.retryPending();
    const items=await readPending();
    if(Number(result?.submitted)>0){
      setState({
        messageText:'Oczekujące zgłoszenia zostały wysłane.',
        messageKind:'success',
        pendingCount:items.length,
        pendingItems:items
      });
    }
    return result;
  }

  async function cancelPending(requestId){
    const removed=await service.cancel(requestId);
    const items=await readPending();
    if(removed){
      setState({
        messageText:'Oczekujące zgłoszenie zostało anulowane.',
        messageKind:'neutral',
        pendingCount:items.length,
        pendingItems:items
      });
    }
    return removed;
  }

  function render(){
    return feedbackPageMarkup({...state,online:Boolean(isOnline())});
  }

  return Object.freeze({
    getState,
    selectKind,
    refresh,
    submit,
    retryPending,
    cancelPending,
    render
  });
}
